import { CSSProperties, cx, LinariaClassName } from '@linaria/core';
import { clamp } from 'lodash-es';
import { memo, ReactElement, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VideoType } from '../../constants/enums';
import { TILE_VIDEO_AVATARS_OPTIONS, VIDEO_CONTAINER_CLASS } from '../../constants/video';
import { videoPlayerReady } from '../../mfcLogic/videoPlayerReady';
import { setupSnapVideo, startVideo, unregisterSnapVideo } from '../../state/state.snapvideo';
import { aspectRatio } from '../../utilities/dimensions';
import { Fade } from '../common/transitions';
import { useUserSelector } from '../Hooks/useUser';
import PreviewPoster from './PreviewPosterImage/PreviewPoster';
import "./LiveSnapStream.linaria.module.css!=!./LiveSnapStream?./LiveSnapStream.linaria.module.css?css=H4sIAAAAAAAAA7WT7a6bMAyG/5+rQJomtVLDCC20ZdJuJUqDA6EhoYmhtGj3PpV+ie7onJ0fQ5GIY7+PbCcOOdZMCkbL1FbtOZyaQ9V6VPJEhDUIBjMBBsH9/P02BtZ7VsZ1XaVDY71CZU3mQHNUHdxDygn5YQ1cq8IQhVD7F+jWM4r9UXV6yJVvND9lUkN/d0PMqPUHG3dDCaooMaNR9P3u7U4P71HlWE6ccc7csj0nE2PYcbEvnG1NToTV1mXfoii6S9AxmtIil/sBHTdeWldn405zhGU+ixbBZc3vAlpuuAXPPJrjZhN2Kgd7qQFc5Z9d4jtvdYuPLiVxnKqe0Vy6VVOFtRSdD64/clX/GkmD3VUgkEiFmbDds2lGxaAMS9bHqkgHQq7SUUN4x5E7suN5AURag8SrM2SCazF7C25fx92MEKFV8xo4D37cvN7whnh0wGviBdewCOh8JDzLz+HsXfmVREhtd0r/t3yUstWBeaNkix/lo4wHzKJwBfXjqeltpZeMbotkXan3jiYX9Td0aHieK1MQDfLCTp9siv7UHooJ/PXsX+luHIMpfrVOBKe3uj8lFbyZlk6pkMYjS6yOaf+ZPst2IK2D28yNwzSLwjXUi4A2/fM2+koe6uqr1Nucv4f9A/+LvnS/BAAA"
const commonRootStyles = "atm_fc_1h6ojuz atm_mk_h2mmj6 atm_h_1h6ojuz atm_9s_1txwivl atm_e2_1osqo2v atm_vy_1osqo2v";
const rootStyles = "atm_2d_r3uz5";
const videoStyles = "atm_fc_1h6ojuz atm_h_1h6ojuz atm_tr_161gdfk atm_1h8aoes_stnw88 atm_5226ix_1dfr4pj atm_ni2ein_57wjg6 atm_1dezsrh_57wjg6 atm_1diiojq_snifut atm_el9jl3_19g57ji atm_1tsyuqg_19g57ji atm_1475ca1_snifut atm_11cfnst_5ol21x atm_1xjfqmj_5ol21x";
// 5% of the tile's width, from the room player's phone size up to 10px
const clipBadgeFontSize = (width: number) => clamp(width * 0.05, 8.45, 10);
const snapshotStyles = "atm_fc_1h6ojuz atm_h_1h6ojuz atm_9s_1txwivl atm_e2_1osqo2v atm_vy_1osqo2v atm_tr_161gdfk";
const videoLowIndex = {
  zIndex: -1,
  pointerEvents: 'none' as const,
  touchActions: 'none' as const
};
type LiveSnapStreamProps = {
  id: number;
  isAway: boolean;
  isBroadcasting: boolean;
  isClubShow: boolean;
  isGroupShow: boolean;
  isLounge: boolean;
  isModel: boolean;
  isNGVideo: boolean;
  isOffline: boolean;
  isPrivateShow: boolean;
  isTruePrivate: boolean;
  banned: boolean;
  videoMuted: boolean;
  phase: string;
  server: string;
  videoID: VideoType;
  channel: number;
  children?: ReactElement;
  fallback?: boolean;
  height?: number;
  hideControls?: boolean;
  volumeOnly?: boolean;
  /** Plain HLS regardless of the viewer's saved stream type */
  forceHls?: boolean;
  noBackground?: boolean;
  staticImage?: boolean;
  className?: LinariaClassName;
  suppressVideo?: boolean;
  width?: number;
  isAnonymous?: boolean;
  onPreviewLoaded?(event): void;
  onVideoPlaying?(): void;
  compact?: boolean;
  onClick?(event: SyntheticEvent): void;
  style?: CSSProperties;
};
const LiveSnapStream = memo((props: LiveSnapStreamProps) => {
  const {
    id,
    banned,
    height,
    width,
    videoID,
    isAway,
    isBroadcasting,
    isClubShow,
    isGroupShow,
    isLounge,
    isModel,
    isNGVideo,
    isOffline,
    isPrivateShow,
    isTruePrivate,
    videoMuted,
    phase,
    channel,
    server,
    children,
    compact,
    className,
    style,
    fallback = false,
    staticImage = false,
    volumeOnly = false,
    hideControls = false,
    forceHls = false,
    suppressVideo = false,
    noBackground = false,
    isAnonymous,
    onPreviewLoaded,
    onVideoPlaying,
    onClick
  } = props;
  const authTks = useUserSelector('me', 'auth');
  const sessionId = useUserSelector('me', 'session');
  // Only for a model the worker lists with clips, and it lists them only
  // for an age-gated visitor: with the option on, play() waits for the
  // model's clip list before it loads the live feed
  const hasVideoAvatar = useUserSelector(id, 'hasVideoAvatar');
  const videoAvatars = videoID === VideoType.ModelTile && hasVideoAvatar ? TILE_VIDEO_AVATARS_OPTIONS : undefined;
  const video = useRef<HTMLDivElement>(undefined);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const safeHeight = height > 0 ? height : 0;
  const safeWidth = width > 0 ? width : 0;
  const {
    height: imageHeight,
    width: imageWidth
  } = aspectRatio({
    height: safeHeight,
    width: safeWidth
  });
  // const isLarge = imageWidth > 320 || imageHeight > 180
  const snapshotDimensionStyles = useMemo(() => ({
    width: imageWidth,
    height: imageHeight
  }), [imageWidth, imageHeight]);
  const shouldStopPlaying = banned || suppressVideo || isTruePrivate || isPrivateShow || isClubShow || isGroupShow || isAway || isOffline || !isBroadcasting;
  const isStaticImage = staticImage || !suppressVideo && !!videoPlaying || !videoPlaying && !shouldStopPlaying;
  useEffect(() => {
    if (videoPlaying && (shouldStopPlaying || fallback)) {
      unregisterSnapVideo(id, videoID);
      setVideoPlaying(false);
    } else if (!videoPlaying && video.current && !(shouldStopPlaying || fallback)) {
      // The player SDK chunk is loaded on demand, so a tile can reach
      // this effect before it has arrived. Wait for it (cancellable),
      // and leave the static snapshot if it never loads.
      let cancelled = false;
      videoPlayerReady().then(() => {
        if (cancelled || !video.current) {
          return;
        }
        if (setupSnapVideo(id, {
          container: video.current,
          hideControls,
          forceMute: videoMuted,
          onlyVolumeControls: volumeOnly,
          forceHls,
          videoAvatars,
          videoID
        }, setVideoPlaying)) {
          startVideo(id);
        }
      }).catch(() => undefined);
      return () => {
        cancelled = true;
      };
    }
  }, [video.current, sessionId, authTks?.ctxenc, shouldStopPlaying, suppressVideo, videoPlaying, fallback]);
  useEffect(() => {
    return () => {
      unregisterSnapVideo(id, videoID);
    };
  }, [id, videoID]);
  useEffect(() => {
    if (typeof onVideoPlaying === 'function' && videoPlaying) {
      onVideoPlaying();
    }
  }, [videoPlaying, onVideoPlaying]);
  const onPreviewLoadedCallback = useCallback(event => {
    if (typeof onPreviewLoaded === 'function') {
      onPreviewLoaded(event);
    }
  }, [onPreviewLoaded]);
  if (fallback) {
    return children || null;
  }
  const showVideo = videoPlaying && !shouldStopPlaying;
  return <div className={cx(!noBackground ? rootStyles : null, commonRootStyles, className)} id={`snap-video-${id}`} onClick={onClick}
  // every grid tile, not only one asking for clips now: a player
  // keeps the clips it was built with when hasVideoAvatar turns off
  style={videoID === VideoType.ModelTile ? {
    ...style,
    '--clip-badge-font-size': clipBadgeFontSize(safeWidth) + 'px'
  } : style}>
            {/* keepVisible: this fade opens only once the video reports
                playing, and the SDK starts the <video> inside it while it is
                still faded out (the tile's own Fade around us also stays
                hidden until the snapshot loads). With visibility:hidden here,
                iOS 17 WebKit's invisible-autoplay rule pauses the video after
                the SDK's play() and the SDK does not retry the AbortError, so
                playing never arrives and the fade never opens. Keeping the
                container laid out as visible and crossfading on opacity alone
                breaks that dependency (device traces, 2026-09-16). */}
            <Fade duration={350} appear={false} in={showVideo} keepVisible>
                <div ref={video} data-testid="LiveSnapStream__Video" style={{
        height: `${imageHeight}px`,
        width: `${imageWidth}px`
      }} className={cx(VIDEO_CONTAINER_CLASS, videoStyles)} />
            </Fade>
            <div style={showVideo ? videoLowIndex : snapshotDimensionStyles} className={snapshotStyles}>
                <PreviewPoster id={id} isAnonymous={isAnonymous} banned={banned} channel={channel} height={safeHeight} isAway={isAway} isBroadcasting={isBroadcasting} isClubShow={isClubShow} isGroupShow={isGroupShow} isLounge={isLounge} isModel={isModel} isNGVideo={isNGVideo} isOffline={isOffline} isPrivateShow={isPrivateShow} isTruePrivate={isTruePrivate} large noBackground={noBackground} phase={phase} server={server} setDimensions staticImage={isStaticImage || showVideo} width={safeWidth} onLoad={onPreviewLoadedCallback} compact={compact} />
            </div>
        </div>;
});
export default LiveSnapStream;